import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import type { AutocompleteOption } from "@/components/ui/autocomplete";
import {
  useAppointment,
  useUpdateAppointmentStatus,
  useVeterinarians,
  type AppointmentStatus,
} from "@/hooks/useAppointments";
import type { GroomingStatus } from "@/hooks/useAppointments";
import {
  useAppointmentItems,
  useAddAppointmentItem,
  useDeleteAppointmentItem,
  type AppointmentItemRow,
} from "@/hooks/useAppointmentItems";
import { useSaleByAppointment, useSaleDetail } from "@/hooks/useSaleDetail";
import { useItems, type Item } from "@/hooks/useItems";
import {
  useAddAppointmentBelonging,
  type AppointmentBelonging,
  useAppointmentBelongings,
  useDeleteAppointmentBelonging,
  useSetAppointmentBelongingReturned,
} from "@/hooks/useAppointmentBelongings";
import { type AppointmentPackageDraft } from "@/components/calendar/AppointmentPackageSection";
import { useAppointmentStatuses } from "@/hooks/useAppointmentStatuses";
import { usePetSummary, type PetWithOwner } from "@/hooks/usePets";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import { usePatientDayEvents } from "@/hooks/usePatientDayEvents";
import type { PatientDayEvent } from "@/components/calendar/types/patient-day-events";
import type { PrescriptionWithItems } from "@/hooks/useMedicalPrescriptions";
import type { Database } from "@/integrations/supabase/types";
import { isItemAllowedForBehavior } from "@/lib/appointments/availability";
import {
  canOpenAppointmentPayment,
  hasAppointmentSale,
  hasOutstandingSaleReceivable,
} from "@/lib/utils/appointment-financial";
import {
  useAppointmentPatientPackages,
  useCommitAppointmentPatientPackages,
  useReplaceAppointmentPatientPackages,
} from "@/hooks/useAppointmentPatientPackage";
import { useReconcileAppointmentFinancial } from "@/hooks/useAppointmentFinancial";
import { summarizeAppointmentBelongingsChecklist } from "@/lib/utils/appointment-belongings";
import { useAllClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { resolveAppointmentTypeBehavior } from "@/lib/appointments/appointment-type-behavior";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";
import { formatPetAge } from "@/lib/date-utils";
import { useVisibleClinics } from "@/hooks/useVisibleClinics";
import { useSwitchClinic } from "@/hooks/useClinics";

// ── Exported types (shared with desktop shell and nested dialogs) ───────────

export type AppointmentPet = {
  id: string;
  name?: string | null;
  photo?: { file_path?: string } | { file_path?: string }[] | null;
  species?: { name?: string | null } | null;
  breed?: { name?: string | null } | null;
  birth_date?: string | null;
  gender?: { name?: string | null } | null;
  sterilization?: { name?: string | null } | null;
  notes?: string | null;
};

export type OwnerAddress = {
  id?: string;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  zip_code?: string | null;
  is_primary?: boolean | null;
  city?: {
    name?: string | null;
    state?: { code?: string | null; name?: string | null } | null;
  } | null;
};

export type DayEventMedicalRecord = Database["public"]["Tables"]["medical_records"]["Row"] & {
  profiles?: { full_name?: string | null } | null;
  clinics?: { name?: string | null } | null;
};

export type DayEventVaccination = {
  id: string;
  application_date: string | null;
  created_at: string | null;
  dose_number: number | null;
  batch_number: string | null;
  next_dose_date: string | null;
  next_dose_completed_at?: string | null;
  notes: string | null;
  adverse_reaction: string | null;
  item_id: { name?: string | null; product_subtype?: { name?: string | null } | null } | null;
};

export type DayEventExamAttachment = {
  id: string;
  type: string | null;
  category: string | null;
  description: string | null;
  exam_date: string | null;
  created_at: string | null;
  file_path: string;
  storage_bucket: string | null;
  file_name: string | null;
  filename: string | null;
  original_name: string | null;
};

export type DayEventGrooming = {
  id: string;
  starts_at: string | null;
  created_at: string | null;
  notes: string | null;
  status: string | null;
  grooming_status: string | null;
  appointment_items?: Array<{
    title_snapshot?: string | null;
    item?: { name?: string | null } | null;
  }>;
};

// ── Internal pure helpers ────────────────────────────────────────────────────

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isAppointmentPet = (value: unknown): value is AppointmentPet =>
  isRecord(value) && typeof value["id"] === "string";

export const isProductOrServiceType = (value?: string | null) => {
  if (!value) return false;
  return value === "PRODUTO" || value === "SERVICO";
};

export const resolvePhotoPath = (
  photo?: { file_path?: string } | { file_path?: string }[] | null,
): string | null => {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
};

const arePackageDraftsEqual = (
  current: AppointmentPackageDraft[],
  next: AppointmentPackageDraft[],
) => {
  if (current.length !== next.length) return false;
  return current.every((draft, index) => {
    const candidate = next[index];
    return (
      draft.id === candidate.id &&
      draft.mode === candidate.mode &&
      draft.petId === candidate.petId &&
      draft.catalogPackageId === candidate.catalogPackageId &&
      draft.patientPackageId === candidate.patientPackageId &&
      draft.sessionsToConsume === candidate.sessionsToConsume &&
      draft.title === candidate.title &&
      draft.price === candidate.price &&
      draft.amountPaid === candidate.amountPaid &&
      draft.amountOutstanding === candidate.amountOutstanding &&
      draft.financialStatus === candidate.financialStatus &&
      draft.remainingSessions === candidate.remainingSessions &&
      draft.totalSessions === candidate.totalSessions &&
      draft.billingIntent === candidate.billingIntent &&
      draft.suggestedChargeAmount === candidate.suggestedChargeAmount
    );
  });
};

const areAppointmentBelongingsEqual = (
  current: AppointmentBelonging[],
  next: AppointmentBelonging[],
) => {
  if (current.length !== next.length) return false;
  return current.every((belonging, index) => {
    const candidate = next[index];
    return (
      belonging.id === candidate.id &&
      belonging.appointment_id === candidate.appointment_id &&
      belonging.name === candidate.name &&
      belonging.returned === candidate.returned &&
      belonging.returned_at === candidate.returned_at &&
      belonging.returned_by === candidate.returned_by &&
      belonging.created_at === candidate.created_at &&
      belonging.created_by === candidate.created_by
    );
  });
};

// ── Hook args / return type ──────────────────────────────────────────────────

export interface AppointmentDetailsControllerArgs {
  appointmentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startInEditMode?: boolean;
}

export function useAppointmentDetailsController({
  appointmentId,
  open,
  onOpenChange,
  startInEditMode = false,
}: AppointmentDetailsControllerArgs) {
  const { t } = useTranslation(["calendar", "common"]);
  const { data: appointment, isLoading: isLoadingAppointment } = useAppointment(appointmentId);
  const { data: appointmentPackages = [] } = useAppointmentPatientPackages(appointmentId);
  const { data: saleByAppointment } = useSaleByAppointment(appointmentId);
  const hasRecordedSale = hasAppointmentSale(saleByAppointment);
  const hasActiveSale = hasRecordedSale && saleByAppointment?.status !== "CANCELADA" && saleByAppointment?.status !== "DEVOLVIDA";
  const {
    updateSaleStatus: cancelLinkedSale,
    isUpdating: isCancellingSale,
  } = useSaleDetail(saleByAppointment?.id ?? null);
  const { data: appointmentItemsData, isLoading: isLoadingAppointmentItems } = useAppointmentItems(
    hasActiveSale ? undefined : appointmentId,
  );
  const { data: clinicProfessionals = [] } = useVeterinarians(appointment?.clinic_id);
  const { data: itemsData } = useItems({ isActive: true });
  const itemsList = useMemo(() => {
    const raw = Array.isArray(itemsData) ? itemsData : (itemsData as { data?: Item[] } | undefined)?.data ?? [];
    return (raw as Item[]).filter((i) => i.type === "PRODUTO" || i.type === "SERVICO");
  }, [itemsData]);
  const availableItemsList = useMemo(
    () => itemsList.filter((item) => isItemAllowedForBehavior(item.allowed_appointment_types, appointment?.clinic_appointment_type?.behavior ?? "padrao")),
    [appointment?.clinic_appointment_type?.behavior, itemsList],
  );
  const itemOptions = useMemo<AutocompleteOption[]>(
    () => availableItemsList.map((item) => ({ value: item.id, label: item.name })),
    [availableItemsList],
  );
  const addAppointmentItem = useAddAppointmentItem();
  const deleteAppointmentItem = useDeleteAppointmentItem();
  const replaceAppointmentPatientPackages = useReplaceAppointmentPatientPackages();
  const commitAppointmentPatientPackages = useCommitAppointmentPatientPackages();
  const reconcileAppointmentFinancial = useReconcileAppointmentFinancial();
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [packageDrafts, setPackageDrafts] = useState<AppointmentPackageDraft[]>([]);
  const [didInitPackageDrafts, setDidInitPackageDrafts] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activePetId, setActivePetId] = useState<string | null>(null);
  const [isOpeningDayEvent, setIsOpeningDayEvent] = useState(false);
  const [selectedDayMedicalRecord, setSelectedDayMedicalRecord] = useState<DayEventMedicalRecord | null>(null);
  const [isDayMedicalRecordModalOpen, setIsDayMedicalRecordModalOpen] = useState(false);
  const [selectedDayVaccination, setSelectedDayVaccination] = useState<DayEventVaccination | null>(null);
  const [isDayVaccinationModalOpen, setIsDayVaccinationModalOpen] = useState(false);
  const [selectedDayExam, setSelectedDayExam] = useState<DayEventExamAttachment | null>(null);
  const [isDayExamModalOpen, setIsDayExamModalOpen] = useState(false);
  const [selectedDayPrescription, setSelectedDayPrescription] = useState<PrescriptionWithItems | null>(null);
  const [isDayPrescriptionModalOpen, setIsDayPrescriptionModalOpen] = useState(false);
  const [selectedDayGrooming, setSelectedDayGrooming] = useState<DayEventGrooming | null>(null);
  const [isDayGroomingModalOpen, setIsDayGroomingModalOpen] = useState(false);
  const [itemScopeDialogOpen, setItemScopeDialogOpen] = useState(false);
  const [cancelSaleDialogOpen, setCancelSaleDialogOpen] = useState(false);
  const [pendingItemAction, setPendingItemAction] = useState<
    | { kind: "add"; itemId: string; title: string; qty: number }
    | { kind: "remove"; itemId: string; title: string; qty: number }
    | null
  >(null);
  const [isApplyingItemScope, setIsApplyingItemScope] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { activeClinicId, clinicsById } = useVisibleClinics();
  const switchClinic = useSwitchClinic();
  const isCrossClinicAppointment = Boolean(
    appointment?.clinic_id && activeClinicId && appointment.clinic_id !== activeClinicId,
  );
  const crossClinicName = appointment?.clinic_id
    ? clinicsById[appointment.clinic_id]?.name ?? ""
    : "";
  const { toast } = useToast();
  const { data: appointmentStatuses = [] } = useAppointmentStatuses("appointment");
  const { data: groomingStatuses = [] } = useAppointmentStatuses("grooming");
  const { data: clinicAppointmentTypes = [] } = useAllClinicAppointmentTypes();
  const { data: permissionsMap, isLoading: isLoadingPermissions } = usePermissionsMap();
  const updateAppointmentStatus = useUpdateAppointmentStatus();
  const [statusDraft, setStatusDraft] = useState<AppointmentStatus | "">("");
  const appointmentTypeBehavior = resolveAppointmentTypeBehavior({
    typeId: appointment?.type,
    embeddedBehavior: appointment?.clinic_appointment_type?.behavior,
    appointmentTypes: clinicAppointmentTypes,
  });
  const isGroomingAppointment = appointmentTypeBehavior === "banho_tosa";
  const [newBelongingName, setNewBelongingName] = useState("");
  const [displayedBelongings, setDisplayedBelongings] = useState<AppointmentBelonging[]>([]);
  const [pendingDeletedBelongingIds, setPendingDeletedBelongingIds] = useState<string[]>([]);
  const [pendingBelongingToggleIds, setPendingBelongingToggleIds] = useState<string[]>([]);
  const { data: appointmentBelongings = [], isLoading: isLoadingAppointmentBelongings } = useAppointmentBelongings(
    appointmentId,
    Boolean(isGroomingAppointment),
  );
  const addAppointmentBelonging = useAddAppointmentBelonging();
  const deleteAppointmentBelonging = useDeleteAppointmentBelonging();
  const setAppointmentBelongingReturned = useSetAppointmentBelongingReturned();
  const canUseGroomingPicker = Boolean(isGroomingAppointment);
  const sidePanelMode = canUseGroomingPicker ? "grooming" : "appointment";
  const sidePanelStatuses = sidePanelMode === "grooming" ? groomingStatuses : appointmentStatuses;
  const sidePanelValue = String(statusDraft || appointment?.status || "agendado");
  const resolvedProfessional = useMemo(() => {
    if (!appointment) return null;
    const fromJoin = (appointment.vet as { full_name?: string | null } | null | undefined)?.full_name?.trim?.();
    if (fromJoin) return { full_name: fromJoin, role: null as string | null };
    const vetUserId = appointment.vet_user_id;
    if (!vetUserId) return null;
    const fromClinic = clinicProfessionals.find((prof) => prof.id === vetUserId) ?? null;
    if (!fromClinic?.full_name) return null;
    return { full_name: fromClinic.full_name, role: fromClinic.role ?? null };
  }, [appointment, clinicProfessionals]);

  const pets = useMemo<AppointmentPet[]>(() => {
    const listUnknown = appointment?.pets;
    if (Array.isArray(listUnknown) && listUnknown.length > 0) {
      return listUnknown.filter(isAppointmentPet);
    }
    const primary = appointment?.pet;
    return primary && isAppointmentPet(primary) ? [primary] : [];
  }, [appointment]);

  useEffect(() => {
    if (!pets.length) {
      setActivePetId((prev) => (prev === null ? prev : null));
      return;
    }
    const desired = appointment?.pet_id ?? pets[0]?.id ?? null;
    if (desired && pets.some((p) => p.id === desired)) {
      setActivePetId((prev) => {
        const nextPetId = prev && pets.some((p) => p.id === prev) ? prev : desired;
        return prev === nextPetId ? prev : nextPetId;
      });
      return;
    }
    setActivePetId((prev) => {
      const nextPetId = prev && pets.some((p) => p.id === prev) ? prev : pets[0]?.id ?? null;
      return prev === nextPetId ? prev : nextPetId;
    });
  }, [appointment, pets]);

  useEffect(() => {
    if (!appointment?.status) return;
    setStatusDraft((prev) => (prev === appointment.status ? prev : appointment.status));
  }, [appointment?.status]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      return;
    }
    if (startInEditMode && appointment) {
      setIsEditing(true);
    }
  }, [appointment, open, startInEditMode]);

  useEffect(() => {
    setDisplayedBelongings((current) => {
      const currentById = new Map(current.map((belonging) => [belonging.id, belonging]));
      const nextBelongings = appointmentBelongings
        .filter((belonging) => !pendingDeletedBelongingIds.includes(belonging.id))
        .map((belonging) => {
          if (!pendingBelongingToggleIds.includes(belonging.id)) {
            return belonging;
          }
          return currentById.get(belonging.id) ?? belonging;
        });
      return areAppointmentBelongingsEqual(current, nextBelongings) ? current : nextBelongings;
    });
  }, [appointmentId, appointmentBelongings, pendingBelongingToggleIds, pendingDeletedBelongingIds]);

  const activePet = useMemo(
    () => pets.find((pet) => pet.id === activePetId) ?? pets[0] ?? null,
    [pets, activePetId],
  );

  const dayEventPermissions = useMemo(
    () => ({
      consultation: permissionsMap?.medical_records?.view === true,
      exam: permissionsMap?.medical_records?.view === true,
      prescription: permissionsMap?.medical_records?.view === true,
      applications: permissionsMap?.applications?.view === true,
      vaccine:
        permissionsMap?.applications?.view === true ||
        permissionsMap?.vaccines?.view === true,
      grooming: permissionsMap?.grooming?.view === true,
    }),
    [permissionsMap],
  );

  const {
    data: patientDayEvents = [],
    isLoading: isLoadingPatientDayEvents,
  } = usePatientDayEvents({
    petId: activePetId,
    clinicId: appointment?.clinic_id,
    appointmentDate: appointment?.starts_at,
    permissions: dayEventPermissions,
    includeGrooming: true,
  });

  const visibleBelongings = useMemo(
    () => displayedBelongings,
    [displayedBelongings],
  );

  const appointmentItems = useMemo(
    () => (appointmentItemsData ?? []) as AppointmentItemRow[],
    [appointmentItemsData],
  );
  const displayStatus = (statusDraft ||
    (appointment?.clinic_appointment_type?.behavior === "banho_tosa" ? appointment?.grooming_status : appointment?.status) ||
    appointment?.status ||
    "agendado") as AppointmentStatus | GroomingStatus;
  const belongingsSummary = useMemo(
    () => summarizeAppointmentBelongingsChecklist(visibleBelongings),
    [visibleBelongings],
  );

  const selectedItem = useMemo(
    () => availableItemsList.find((item) => item.id === selectedItemId) ?? null,
    [availableItemsList, selectedItemId],
  );
  const incompatibleAppointmentItems = useMemo(
    () =>
      appointmentItems.filter((row) => {
        const matchingItem = itemsList.find((item) => item.id === row.item_id);
        return matchingItem ? !isItemAllowedForBehavior(matchingItem.allowed_appointment_types, appointment?.clinic_appointment_type?.behavior ?? "padrao") : false;
      }),
    [appointment?.clinic_appointment_type?.behavior, appointmentItems, itemsList],
  );

  useEffect(() => {
    if (selectedItemId && !selectedItem) {
      setSelectedItemId("");
      setSearchValue("");
    }
  }, [selectedItem, selectedItemId]);

  const startsAt = appointment ? new Date(appointment.starts_at) : null;
  const endsAt = appointment ? new Date(appointment.ends_at) : null;

  const ownerPhones =
    (appointment?.owner?.phones as
      | { phone_number?: string; is_primary?: boolean; is_whatsapp?: boolean | null }[]
      | undefined) ??
    [];
  const ownerEmails =
    (appointment?.owner?.emails as { email?: string; is_primary?: boolean }[] | undefined) ?? [];

  const primaryPhone = ownerPhones.find((phone) => phone.is_primary) ?? ownerPhones[0];
  const primaryEmail = ownerEmails.find((email) => email.is_primary) ?? ownerEmails[0];
  const ownerAddresses = useMemo<OwnerAddress[]>(() => {
    const maybeAddresses = (appointment?.owner as unknown as { addresses?: unknown } | null | undefined)
      ?.addresses;
    return Array.isArray(maybeAddresses) ? (maybeAddresses as OwnerAddress[]) : [];
  }, [appointment?.owner]);
  const primaryAddress = useMemo(
    () => ownerAddresses.find((address) => address?.is_primary) ?? ownerAddresses[0] ?? null,
    [ownerAddresses],
  );
  const ownerAddressLabel = useMemo(() => {
    if (!primaryAddress) return "";
    const street = primaryAddress.street?.trim() ?? "";
    const number = primaryAddress.number?.trim() ?? "";
    const neighborhood = primaryAddress.neighborhood?.trim() ?? "";
    const complement = primaryAddress.complement?.trim() ?? "";
    const city = primaryAddress.city?.name?.trim() ?? "";
    const stateCode = primaryAddress.city?.state?.code?.trim() ?? "";
    const zip = primaryAddress.zip_code?.trim() ?? "";
    const line1 = [street, number ? `nº ${number}` : "", complement].filter(Boolean).join(", ");
    const line2 = [neighborhood, city, stateCode ? `- ${stateCode}` : ""].filter(Boolean).join(" ");
    const result = [line1, line2, zip ? `CEP ${zip}` : ""].filter(Boolean).join(" · ");
    return result;
  }, [primaryAddress]);

  const isLoading = isLoadingAppointment || (!hasActiveSale && isLoadingAppointmentItems);

  const hasFollowingRecurrenceOccurrences = async () => {
    if (!appointment?.recurrence_id || !appointment?.starts_at) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: followingRows, error } = await (supabase as any)
      .from("appointments")
      .select("id")
      .eq("recurrence_id", appointment.recurrence_id)
      .gt("starts_at", appointment.starts_at)
      .is("deleted_at", null)
      .limit(1);
    if (error) throw error;
    return (followingRows ?? []).length > 0;
  };

  const handleAddItem = async () => {
    if (!appointmentId || !selectedItem) return;
    if (!quantity || quantity < 1) return;

    if (appointment?.recurrence_id) {
      try {
        const hasFollowing = await hasFollowingRecurrenceOccurrences();
        if (hasFollowing) {
          setPendingItemAction({
            kind: "add",
            itemId: selectedItem.id,
            title: selectedItem.name,
            qty: quantity,
          });
          setItemScopeDialogOpen(true);
          return;
        }
      } catch (error) {
        toast({
          title: "Erro ao verificar recorrência",
          description: error instanceof Error ? error.message : "Tente novamente.",
          variant: "destructive",
        });
        return;
      }
    }

    await addAppointmentItem.mutateAsync({
      appointmentId,
      item: {
        item_id: selectedItem.id,
        title_snapshot: selectedItem.name,
        qty: quantity,
      },
    });

    setSelectedItemId("");
    setSearchValue("");
    setQuantity(1);
  };

  const handleRemoveItem = async (row: AppointmentItemRow) => {
    if (!appointmentId) return;
    if (appointment?.recurrence_id) {
      try {
        const hasFollowing = await hasFollowingRecurrenceOccurrences();
        if (hasFollowing) {
          setPendingItemAction({
            kind: "remove",
            itemId: row.item_id,
            title: row.title_snapshot,
            qty: row.qty,
          });
          setItemScopeDialogOpen(true);
          return;
        }
      } catch (error) {
        toast({
          title: "Erro ao verificar recorrência",
          description: error instanceof Error ? error.message : "Tente novamente.",
          variant: "destructive",
        });
        return;
      }
    }
    await deleteAppointmentItem.mutateAsync({ itemId: row.id, appointmentId });
  };

  const handleAddBelonging = async () => {
    const name = newBelongingName.trim();
    if (!appointmentId || !name) return;

    const optimisticId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `optimistic-belonging-${Date.now()}`;
    const optimisticBelonging: AppointmentBelonging = {
      id: optimisticId,
      appointment_id: appointmentId,
      name,
      returned: false,
      returned_at: null,
      returned_by: null,
      created_at: new Date().toISOString(),
      created_by: "",
    };

    setDisplayedBelongings((current) => [...current, optimisticBelonging]);
    setNewBelongingName("");

    try {
      const savedBelonging = await addAppointmentBelonging.mutateAsync({ appointmentId, name });
      setDisplayedBelongings((current) =>
        current.map((belonging) => (belonging.id === optimisticId ? savedBelonging : belonging)),
      );
    } catch (error) {
      setDisplayedBelongings((current) => current.filter((belonging) => belonging.id !== optimisticId));
      setNewBelongingName(name);
      throw error;
    }
  };

  const handleToggleBelongingReturned = async (itemId: string, returned: boolean) => {
    if (!appointmentId) return;

    const previousReturned = displayedBelongings.find((item) => item.id === itemId)?.returned ?? false;
    setPendingBelongingToggleIds((current) => [...current, itemId]);
    setDisplayedBelongings((current) =>
      current.map((item) => (item.id === itemId ? { ...item, returned } : item)),
    );

    try {
      await setAppointmentBelongingReturned.mutateAsync({ appointmentId, itemId, returned });
      setPendingBelongingToggleIds((current) => current.filter((id) => id !== itemId));
    } catch (error) {
      setPendingBelongingToggleIds((current) => current.filter((id) => id !== itemId));
      setDisplayedBelongings((current) =>
        current.map((item) => (item.id === itemId ? { ...item, returned: previousReturned } : item)),
      );
      toast({
        title: "Erro ao atualizar devolução",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBelonging = async (itemId: string) => {
    if (!appointmentId) return;

    const existingBelonging = displayedBelongings.find((item) => item.id === itemId) ?? null;
    if (!existingBelonging) return;

    setPendingDeletedBelongingIds((current) => [...current, itemId]);
    setDisplayedBelongings((current) => current.filter((item) => item.id !== itemId));

    try {
      await deleteAppointmentBelonging.mutateAsync({ appointmentId, itemId });
      setPendingDeletedBelongingIds((current) => current.filter((id) => id !== itemId));
    } catch (error) {
      setPendingDeletedBelongingIds((current) => current.filter((id) => id !== itemId));
      setDisplayedBelongings((current) => [...current, existingBelonging]);
    }
  };

  const applyItemActionSingle = async () => {
    if (!pendingItemAction || !appointmentId) return;
    if (pendingItemAction.kind === "add") {
      await addAppointmentItem.mutateAsync({
        appointmentId,
        item: {
          item_id: pendingItemAction.itemId,
          title_snapshot: pendingItemAction.title,
          qty: pendingItemAction.qty,
        },
      });
      return;
    }
    const target = appointmentItems.find(
      (item) =>
        item.item_id === pendingItemAction.itemId &&
        item.title_snapshot === pendingItemAction.title &&
        item.qty === pendingItemAction.qty,
    );
    if (!target) return;
    await deleteAppointmentItem.mutateAsync({ itemId: target.id, appointmentId });
  };

  const applyItemActionFuture = async () => {
    if (!appointment?.recurrence_id || !pendingItemAction) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: futureAppointments, error: futureError } = await (supabase as any)
      .from("appointments")
      .select("id")
      .eq("recurrence_id", appointment.recurrence_id)
      .gte("starts_at", appointment.starts_at)
      .is("deleted_at", null);
    if (futureError) throw futureError;

    const appointmentIds = (futureAppointments ?? []).map((row: { id: string }) => row.id);
    if (appointmentIds.length === 0) return;

    if (pendingItemAction.kind === "add") {
      const payload = appointmentIds.map((id: string) => ({
        appointment_id: id,
        item_id: pendingItemAction.itemId,
        qty: pendingItemAction.qty,
        title_snapshot: pendingItemAction.title,
      }));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).from("appointment_items").insert(payload);
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: templateError } = await (supabase as any).from("recurrence_items").insert({
        recurrence_id: appointment.recurrence_id,
        item_id: pendingItemAction.itemId,
        qty: pendingItemAction.qty,
        title_snapshot: pendingItemAction.title,
        sort_order: 999,
      });
      if (templateError) throw templateError;
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: deleteError } = await (supabase as any)
      .from("appointment_items")
      .delete()
      .in("appointment_id", appointmentIds)
      .eq("item_id", pendingItemAction.itemId)
      .eq("title_snapshot", pendingItemAction.title);
    if (deleteError) throw deleteError;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: templateDeleteError } = await (supabase as any)
      .from("recurrence_items")
      .delete()
      .eq("recurrence_id", appointment.recurrence_id)
      .eq("item_id", pendingItemAction.itemId)
      .eq("title_snapshot", pendingItemAction.title);
    if (templateDeleteError) throw templateDeleteError;
  };

  const handleScopeAction = async (scope: "single" | "future") => {
    if (!pendingItemAction) return;
    setIsApplyingItemScope(true);
    try {
      if (scope === "single") {
        await applyItemActionSingle();
      } else {
        await applyItemActionFuture();
      }
      await queryClient.invalidateQueries({ queryKey: ["appointments"] });
      await queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
      await queryClient.invalidateQueries({ queryKey: ["appointment-items", appointmentId] });
      setSelectedItemId("");
      setSearchValue("");
      setQuantity(1);
      setPendingItemAction(null);
      setItemScopeDialogOpen(false);
      toast({
        title: scope === "single" ? "Item atualizado nesta ocorrência" : "Item aplicado nesta e nas próximas ocorrências",
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar itens da recorrência",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsApplyingItemScope(false);
    }
  };

  const displayItems = useMemo(() => {
    if (hasActiveSale && saleByAppointment?.items?.length) {
      return saleByAppointment.items
        .filter((si) => {
          const itemType = isRecord(si.item) && typeof si.item.type === "string" ? si.item.type : null;
          const groomingType = isRecord(si.grooming_item) && typeof si.grooming_item.type === "string"
            ? si.grooming_item.type
            : null;
          return isProductOrServiceType(itemType) || isProductOrServiceType(groomingType);
        })
        .map((si) => ({
          id: si.id,
          title_snapshot: si.item_name,
          qty: si.quantity,
          _displayPrice: si.unit_price,
          _displayLineTotal: si.line_total,
          item:
            (isRecord(si.item) && typeof si.item.type === "string")
              ? { type: si.item.type }
              : (isRecord(si.grooming_item) && typeof si.grooming_item.type === "string")
                ? { type: si.grooming_item.type }
                : null,
        }));
    }
    return appointmentItems.map((ai) => {
      const displayPrice = ai.item?.sale_price ?? itemsList.find((i) => i.id === ai.item_id)?.sale_price ?? 0;
      const displayLineTotal = (ai.qty ?? 1) * displayPrice;
      return { ...ai, _displayPrice: displayPrice, _displayLineTotal: displayLineTotal };
    });
  }, [hasActiveSale, saleByAppointment?.items, appointmentItems, itemsList]);

  const paidPackageItems = useMemo(() => {
    const bySale = (saleByAppointment?.items ?? [])
      .filter((si) => {
        const itemType = isRecord(si.item) && typeof si.item.type === "string" ? si.item.type : null;
        const groomingType = isRecord(si.grooming_item) && typeof si.grooming_item.type === "string"
          ? si.grooming_item.type
          : null;
        return (
          Number(si.line_total ?? 0) >= 0 &&
          !isProductOrServiceType(itemType) &&
          !isProductOrServiceType(groomingType)
        );
      })
      .map((si) => ({
        id: si.id,
        title_snapshot: si.item_name,
        qty: si.quantity,
        _displayPrice: si.unit_price,
        _displayLineTotal: si.line_total,
        item: { type: "PACOTE" },
      }));

    if (hasActiveSale) return bySale;

    const byDraft = packageDrafts
      .filter((draft) => draft.id || draft.title)
      .map((draft, index) => ({
        id: `draft-${draft.id ?? draft.title ?? `index-${index}`}`,
        title_snapshot: draft.title ?? "Pacote",
        qty: 1,
        _displayPrice: Number(draft.amountPaid ?? 0),
        _displayLineTotal: Number(draft.amountPaid ?? 0),
        item: { type: "PACOTE" },
      }));

    const seenDraftTitles = new Set(byDraft.map((row) => `${row.title_snapshot}|${row._displayLineTotal}`));
    const merged = [...byDraft];
    for (const row of bySale) {
      const signature = `${row.title_snapshot}|${row._displayLineTotal}`;
      if (!seenDraftTitles.has(signature)) merged.push(row);
      seenDraftTitles.add(signature);
    }
    return merged;
  }, [hasActiveSale, packageDrafts, saleByAppointment?.items]);

  const syncPackageDrafts = useCallback((packages: typeof appointmentPackages) => {
    const nextDrafts = packages.map((pkg) => ({
      id: pkg.id,
      mode: pkg.mode,
      petId: pkg.pet_id,
      catalogPackageId: pkg.catalog_package_id,
      patientPackageId: pkg.patient_package_id,
      sessionsToConsume: pkg.sessions_to_consume,
      title: pkg.patient_package?.catalog_package?.item?.name ?? pkg.catalog_package?.item?.name ?? "Pacote",
      price: pkg.patient_package?.catalog_package?.item?.sale_price ?? pkg.catalog_package?.item?.sale_price ?? 0,
      amountPaid: pkg.patient_package?.amount_paid ?? 0,
      amountOutstanding: pkg.patient_package?.outstanding_amount ?? 0,
      financialStatus: pkg.patient_package?.status_financeiro ?? "pendente",
      controlsSessions: pkg.patient_package?.catalog_package?.controls_sessions ?? pkg.catalog_package?.controls_sessions ?? true,
      remainingSessions: pkg.patient_package?.sessions_remaining ?? pkg.catalog_package?.default_sessions ?? 0,
      totalSessions: pkg.patient_package?.sessions_total ?? pkg.catalog_package?.default_sessions ?? 0,
      billingIntent: pkg.billing_intent,
      suggestedChargeAmount: pkg.suggested_charge_amount,
      allowedAppointmentTypes:
        pkg.patient_package?.catalog_package?.item?.allowed_appointment_types ??
        pkg.catalog_package?.item?.allowed_appointment_types ??
        [],
    }));
    setPackageDrafts((current) => (arePackageDraftsEqual(current, nextDrafts) ? current : nextDrafts));
  }, []);

  useEffect(() => {
    if (didInitPackageDrafts && replaceAppointmentPatientPackages.isPending) return;
    syncPackageDrafts(appointmentPackages);
    setDidInitPackageDrafts(true);
  }, [appointmentPackages, didInitPackageDrafts, replaceAppointmentPatientPackages.isPending, syncPackageDrafts]);

  const persistPackageDrafts = useCallback(async (nextDrafts: AppointmentPackageDraft[]) => {
    if (!appointmentId || !appointment?.clinic_id) return;

    for (const draft of nextDrafts) {
      if (draft.mode === "new" && !draft.catalogPackageId) {
        throw new Error("Selecione um pacote do catálogo para cada novo pacote adicionado.");
      }
      if (draft.mode === "existing" && !draft.patientPackageId) {
        throw new Error("Selecione um pacote existente para cada utilização adicionada.");
      }
      if (draft.billingIntent === "partial_now" && (draft.suggestedChargeAmount ?? 0) <= 0) {
        throw new Error("Informe um valor valido para cobranca parcial.");
      }
    }

    await replaceAppointmentPatientPackages.mutateAsync({
      appointmentId,
      links: nextDrafts.map((draft) => ({
        id: draft.id,
        appointmentId,
        clinicId: appointment.clinic_id,
        petId: draft.petId,
        mode: draft.mode,
        patientPackageId: draft.patientPackageId,
        catalogPackageId: draft.catalogPackageId,
        sessionsToConsume: draft.sessionsToConsume,
        consumptionStatus: "planned",
        billingIntent: draft.billingIntent,
        suggestedChargeAmount: draft.suggestedChargeAmount,
      })),
    });

    await reconcileAppointmentFinancial.mutateAsync(appointmentId);

    if (appointment.status === "finalizado") {
      await commitAppointmentPatientPackages.mutateAsync(appointmentId);
    }
  }, [
    appointment?.clinic_id,
    appointment?.status,
    appointmentId,
    commitAppointmentPatientPackages,
    replaceAppointmentPatientPackages,
    reconcileAppointmentFinancial,
  ]);

  const handlePackageDraftChange = useCallback((nextDrafts: AppointmentPackageDraft[]) => {
    setPackageDrafts(nextDrafts);
    void persistPackageDrafts(nextDrafts).catch((error) => {
      syncPackageDrafts(appointmentPackages);
      toast({
        title: "Erro ao atualizar pacotes",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    });
  }, [appointmentPackages, persistPackageDrafts, syncPackageDrafts, toast]);

  const servicesTotal = useMemo(
    () => displayItems.reduce((sum, row) => sum + row._displayLineTotal, 0),
    [displayItems],
  );
  const packageSuggestedChargeTotal = useMemo(() => {
    if (hasActiveSale) {
      return paidPackageItems.reduce((total, row) => total + Math.max(0, row._displayLineTotal), 0);
    }
    return packageDrafts.reduce((total, draft) => {
      if (draft.billingIntent !== "integral_now" && draft.billingIntent !== "partial_now") {
        return total;
      }
      return total + Math.max(0, draft.suggestedChargeAmount ?? 0);
    }, 0);
  }, [packageDrafts, hasActiveSale, paidPackageItems]);
  const servicesBalanceTotal = useMemo(() => servicesTotal, [servicesTotal]);
  const appointmentBalanceTotal = useMemo(
    () => servicesBalanceTotal + packageSuggestedChargeTotal,
    [packageSuggestedChargeTotal, servicesBalanceTotal],
  );
  const saleHasOutstandingReceivable = hasOutstandingSaleReceivable(saleByAppointment);
  const persistedFinancialStatus = deriveFinancialStatusWithLegacyFallback(
    appointment?.appointment_financial,
    {
      status: appointment?.status,
      grooming_status: appointment?.grooming_status,
    },
    saleByAppointment,
  );
  const effectiveFinancialStatus =
    hasActiveSale && !saleHasOutstandingReceivable
      ? "pago"
      : persistedFinancialStatus;
  const isPaymentSectionSettled = effectiveFinancialStatus === "pago";
  const isPackageSectionLocked =
    replaceAppointmentPatientPackages.isPending ||
    commitAppointmentPatientPackages.isPending ||
    hasActiveSale;

  const handleEditClick = () => {
    if (!appointment) return;
    setIsEditing(true);
  };

  const handleCloseEdit = () => {
    setIsEditing(false);
  };

  const billablePackageDrafts = packageDrafts.filter(
    (draft) => draft.billingIntent === "integral_now" || draft.billingIntent === "partial_now",
  );
  const canShowPaymentCta = canOpenAppointmentPayment({
    sale: hasActiveSale ? saleByAppointment : null,
    appointmentItems,
    packageCharges: billablePackageDrafts,
    outstandingBalance: appointmentBalanceTotal,
  });
  const canResumeAttendance = appointment?.status === "em_atendimento";
  const resumePetId = activePetId ?? appointment?.pet_id ?? null;
  const quickStartSource = location.pathname.includes("flowboard") ? "flowboard" : "calendar";

  const handleGoToPayment = () => {
    if (!appointment || !canShowPaymentCta) return;

    if (hasActiveSale && saleByAppointment?.id && saleHasOutstandingReceivable) {
      onOpenChange(false);
      navigate("/sales", { state: { openSaleId: saleByAppointment.id } });
      return;
    }

    const ownerNameVal = appointment.owner?.name ?? "";
    const primaryPet = pets.find((pet) => pet.id === appointment.pet_id) ?? pets[0] ?? null;
    const petName = primaryPet?.name ?? "";
    const petsForPrefill = pets.map((pet) => ({ petId: pet.id, petName: pet.name ?? "" }));
    const appointmentItemsPrefill = appointmentItems.map((item) => ({
      item_id: item.item_id,
      qty: item.qty,
      title_snapshot: item.title_snapshot,
      item: item.item ? { name: item.item.name, sale_price: item.item.sale_price ?? null, type: item.item.type ?? null } : null,
    }));
    const suggestedPackageCharges = packageDrafts
      .filter((draft) => draft.billingIntent === "integral_now" || draft.billingIntent === "partial_now")
      .map((draft) => ({
        appointmentPatientPackageId: draft.id ?? null,
        patientPackageId: draft.patientPackageId ?? null,
        catalogPackageId: draft.catalogPackageId ?? null,
        packageMode: draft.mode,
        itemId:
          appointmentPackages.find((pkg) => pkg.id === draft.id)?.catalog_package?.item?.id ??
          appointmentPackages.find((pkg) => pkg.id === draft.id)?.patient_package?.catalog_package?.item?.id ??
          null,
        petId: draft.petId,
        title: draft.title ?? "Pacote",
        fullPrice: draft.price ?? 0,
        outstandingAmount: draft.amountOutstanding ?? draft.price ?? 0,
        suggestedChargeAmount: Math.max(0, draft.suggestedChargeAmount ?? 0),
        billingIntent: draft.billingIntent,
      }));

    onOpenChange(false);
    navigate("/sales", {
      state: {
        prefillAppointmentForSale: {
          appointmentId: appointment.id,
          ownerId: appointment.owner_id,
          ownerName: ownerNameVal,
          petId: primaryPet?.id ?? appointment.pet_id,
          petName,
          pets: petsForPrefill,
          vet_user_id: appointment.vet_user_id ?? null,
          vet: { full_name: resolvedProfessional?.full_name ?? null },
          appointmentItems: appointmentItemsPrefill,
          suggestedPackageCharges,
        },
      },
    });
  };

  const { data: petSummary, isLoading: isLoadingPetSummary } = usePetSummary(activePetId ?? undefined);
  const activePetFallback = useMemo(
    () => (activePet ? (activePet as unknown as PetWithOwner) : null),
    [activePet],
  );
  const pet = (petSummary ?? activePetFallback) as PetWithOwner | null;
  const petPhoto = resolvePhotoPath((pet?.photo ?? null) as unknown as AppointmentPet["photo"]);
  const ownerNotes = typeof appointment?.owner?.notes === "string" && appointment.owner.notes.trim() ? (appointment.owner.notes as string) : "";
  const petNotes = (pet?.notes as string | null | undefined)?.trim?.() ? (pet?.notes as string) : "";
  const allergies = petSummary?.allergies ?? [];
  const temperaments = petSummary?.temperaments ?? [];
  const ownerName = typeof appointment?.owner?.name === "string" && appointment.owner.name.trim() ? (appointment.owner.name as string) : "";
  const activePetName = activePet?.name?.trim?.() ? (activePet.name as string) : "";
  const petsLabel = useMemo(() => {
    const names = pets
      .map((petItem) => petItem?.name?.trim?.() ?? "")
      .filter(Boolean);
    if (names.length > 0) return names.join(", ");
    if (activePetName) return activePetName;
    return "";
  }, [activePetName, pets]);
  const titleText = useMemo(() => {
    if (ownerName && petsLabel) return `${ownerName} — ${petsLabel}`;
    if (ownerName) return ownerName;
    if (petsLabel) return petsLabel;
    return "Agendamento";
  }, [ownerName, petsLabel]);

  const handleOpenExamFileFromModal = async (documentAttachment: DayEventExamAttachment) => {
    if (!documentAttachment.file_path || !documentAttachment.storage_bucket) {
      toast({ title: "Arquivo não disponível.", variant: "destructive" });
      return;
    }

    const storagePath =
      normalizeStoragePath(documentAttachment.file_path, documentAttachment.storage_bucket) ??
      documentAttachment.file_path;
    if (storagePath.startsWith("http")) {
      window.open(storagePath, "_blank", "noopener,noreferrer");
      return;
    }

    const { data, error } = await supabase.storage
      .from(documentAttachment.storage_bucket)
      .createSignedUrl(storagePath, 3600);

    if (error || !data?.signedUrl) {
      toast({ title: "Não foi possível abrir o arquivo.", variant: "destructive" });
      return;
    }

    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenDayEvent = async (event: PatientDayEvent) => {
    setIsOpeningDayEvent(true);

    try {
      if (event.type === "consultation") {
        const recordId = event.medicalRecordId ?? event.sourceId;
        const { data, error } = await supabase
          .from("medical_records")
          .select(`*, profiles!fk_vet_user(full_name), clinics(name)`)
          .eq("id", recordId)
          .single();
        if (error) throw error;
        setSelectedDayMedicalRecord(data as DayEventMedicalRecord);
        setIsDayMedicalRecordModalOpen(true);
        return;
      }

      if (event.type === "vaccine") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from("pet_applications")
          .select(`id, application_date, created_at, dose_number, batch_number, next_dose_date, notes, adverse_reaction, item_id:items!pet_applications_item_id_fkey(name, product_subtype:product_subtypes(name))`)
          .eq("id", event.sourceId)
          .single();
        if (error) throw error;
        setSelectedDayVaccination(data as DayEventVaccination);
        setIsDayVaccinationModalOpen(true);
        return;
      }

      if (event.type === "exam") {
        const { data, error } = await supabase
          .from("pet_attachments")
          .select("id, type, category, description, exam_date, created_at, file_path, storage_bucket, file_name, filename, original_name")
          .eq("id", event.sourceId)
          .single();
        if (error) throw error;
        setSelectedDayExam(data as DayEventExamAttachment);
        setIsDayExamModalOpen(true);
        return;
      }

      if (event.type === "prescription") {
        const { data, error } = await supabase
          .from("medical_prescriptions")
          .select(`*, items:medical_prescription_items(*), exam_items:medical_prescription_exam_items(*), vet:profiles!medical_prescriptions_vet_user_id_fkey(id, full_name, address), clinic:clinics(id, name)`)
          .eq("id", event.sourceId)
          .single();
        if (error) throw error;
        setSelectedDayPrescription(data as PrescriptionWithItems);
        setIsDayPrescriptionModalOpen(true);
        return;
      }

      if (event.type === "grooming") {
        const { data, error } = await supabase
          .from("appointments")
          .select(`id, starts_at, created_at, notes, status, appointment_items(title_snapshot, item:items(name))`)
          .eq("id", event.sourceId)
          .single();
        if (error) throw error;
        setSelectedDayGrooming(data as DayEventGrooming);
        setIsDayGroomingModalOpen(true);
      }
    } catch (error) {
      toast({
        title: t("calendar:appointmentDetails.openEventError"),
        variant: "destructive",
      });
    } finally {
      setIsOpeningDayEvent(false);
    }
  };

  const handleStatusChange = (value: string) => {
    if (!appointment?.id) return;
    const nextStatus = value as AppointmentStatus;
    const previousStatus = statusDraft || appointment.status;
    setStatusDraft(nextStatus);
    if (nextStatus === previousStatus) return;
    updateAppointmentStatus.mutate(
      { id: appointment.id, status: nextStatus as AppointmentStatus },
      { onError: () => { setStatusDraft(previousStatus); } },
    );
  };

  // Derived pet info for mobile patient card
  const activePetSpeciesBreedAge = useMemo(() => {
    if (!activePet) return "";
    return [
      activePet.species?.name,
      activePet.breed?.name,
      activePet.birth_date ? formatPetAge(activePet.birth_date) : null,
    ].filter(Boolean).join(" · ");
  }, [activePet]);

  return {
    // Args pass-through
    appointmentId,
    open,
    onOpenChange,
    // Appointment data
    appointment,
    isLoading,
    isLoadingAppointment,
    pets,
    activePet,
    activePetId,
    setActivePetId,
    activePetName,
    activePetSpeciesBreedAge,
    petPhoto,
    pet,
    petNotes,
    allergies,
    temperaments,
    isLoadingPetSummary,
    // Owner data
    ownerName,
    ownerNotes,
    ownerAddressLabel,
    primaryPhone,
    primaryEmail,
    primaryAddress,
    // Appointment state / derived
    titleText,
    displayStatus,
    startsAt,
    endsAt,
    isGroomingAppointment,
    appointmentTypeBehavior,
    sidePanelMode,
    sidePanelValue,
    sidePanelStatuses,
    canResumeAttendance,
    resumePetId,
    quickStartSource,
    // Status
    appointmentStatuses,
    groomingStatuses,
    statusDraft,
    setStatusDraft,
    updateAppointmentStatus,
    // Items
    appointmentItems,
    displayItems,
    incompatibleAppointmentItems,
    isLoadingAppointmentItems,
    hasActiveSale,
    saleByAppointment,
    saleHasOutstandingReceivable,
    itemOptions,
    availableItemsList,
    selectedItemId,
    setSelectedItemId,
    searchValue,
    setSearchValue,
    quantity,
    setQuantity,
    selectedItem,
    addAppointmentItem,
    deleteAppointmentItem,
    // Financial
    servicesBalanceTotal,
    packageSuggestedChargeTotal,
    appointmentBalanceTotal,
    isPaymentSectionSettled,
    canShowPaymentCta,
    // Packages
    appointmentPackages,
    packageDrafts,
    setPackageDrafts,
    paidPackageItems,
    isPackageSectionLocked,
    // Belongings
    visibleBelongings,
    displayedBelongings,
    newBelongingName,
    setNewBelongingName,
    belongingsSummary,
    isLoadingAppointmentBelongings,
    addAppointmentBelonging,
    // Professional
    resolvedProfessional,
    // Day events
    patientDayEvents,
    isLoadingPatientDayEvents,
    dayEventPermissions,
    // Permissions
    permissionsMap,
    isLoadingPermissions,
    // Editing
    isEditing,
    setIsEditing,
    // Day event dialogs state
    selectedDayMedicalRecord,
    setSelectedDayMedicalRecord,
    isDayMedicalRecordModalOpen,
    setIsDayMedicalRecordModalOpen,
    selectedDayVaccination,
    isDayVaccinationModalOpen,
    setIsDayVaccinationModalOpen,
    selectedDayExam,
    isDayExamModalOpen,
    setIsDayExamModalOpen,
    selectedDayPrescription,
    isDayPrescriptionModalOpen,
    setIsDayPrescriptionModalOpen,
    selectedDayGrooming,
    isDayGroomingModalOpen,
    setIsDayGroomingModalOpen,
    isOpeningDayEvent,
    // Item scope dialog
    itemScopeDialogOpen,
    setItemScopeDialogOpen,
    pendingItemAction,
    setPendingItemAction,
    isApplyingItemScope,
    // Cancel sale dialog
    cancelSaleDialogOpen,
    setCancelSaleDialogOpen,
    isCancellingSale,
    cancelLinkedSale,
    // Handlers
    handleEditClick,
    handleCloseEdit,
    handleAddItem,
    handleRemoveItem,
    handleAddBelonging,
    handleToggleBelongingReturned,
    handleDeleteBelonging,
    handlePackageDraftChange,
    handleOpenDayEvent,
    handleOpenExamFileFromModal,
    handleGoToPayment,
    handleScopeAction,
    handleStatusChange,
    // Multi-clinic
    isCrossClinicAppointment,
    crossClinicName,
    switchClinic,
  } as const;
}

export type AppointmentDetailsController = ReturnType<typeof useAppointmentDetailsController>;
